import { BaseApi } from '@Features/Base/BaseApi/BaseApi'

export type BundleCatalogPayload = {
	page?: number
	perPage?: number
	query?: string
}

export type BundleCheckoutPayload = {
	bundle_id: number
	payment_method: 'wallet' | 'scratchCard' | 'free'
	code?: string
	promocode?: string
}

export const bundleApi = BaseApi.enhanceEndpoints({
	addTagTypes: [
		'Bundles',
		'Bundle',
		'MyBundles',
		'StandaloneCourses',
		'StandaloneCourse',
		'MyStandaloneCourses',
		'StandaloneAccess',
		'Profile',
	],
}).injectEndpoints({
	endpoints: builder => ({
		getBundles: builder.query<ApiResponse<Bundle[]>, BundleCatalogPayload>({
			query: payload => ({
				url: '/bundles',
				params: {
					page: payload.page ?? 1,
					perPage: payload.perPage ?? 12,
					...(payload.query ? { query: payload.query } : {}),
				},
			}),
			providesTags: ['Bundles'],
		}),
		getFeaturedBundles: builder.query<ApiResponse<Bundle[]>, void>({
			query: () => '/bundles/featured',
			providesTags: ['Bundles'],
		}),
		getBundleByLabel: builder.query<ApiResponse<Bundle>, string>({
			query: label => `/bundles/details/${label}`,
			providesTags: (result, error, label) => [{ type: 'Bundle', id: label }],
		}),
		getMyBundles: builder.query<ApiResponse<MyBundleEnrollment[]>, BundleCatalogPayload>({
			query: payload => ({
				url: '/bundles/mine',
				params: { page: payload.page ?? 1, perPage: payload.perPage ?? 12 },
			}),
			providesTags: ['MyBundles'],
		}),
		// buying a bundle changes member-course ownership too, so the
		// standalone-course caches must refetch alongside the bundle ones
		checkoutBundle: builder.mutation<ApiResponse<BundleCheckoutResult>, BundleCheckoutPayload>({
			query: payload => ({
				url: '/checkout/bundle',
				method: 'POST',
				body: payload,
			}),
			invalidatesTags: [
				'Bundle',
				'Bundles',
				'MyBundles',
				'StandaloneCourse',
				'StandaloneCourses',
				'MyStandaloneCourses',
				'StandaloneAccess',
				'Profile',
			],
		}),
		verifyBundlePromocode: builder.query<ApiResponse<{ value: number }>, { bundle_id: number; code: string }>({
			query: payload => ({
				url: '/bundles/promocodes/verify',
				method: 'POST',
				body: payload,
			}),
		}),
		getBundleInvoices: builder.query<ApiResponse<BundleInvoiceRow[]>, BundleCatalogPayload>({
			query: payload => ({
				url: '/bundles/invoices',
				params: { page: payload.page ?? 1, perPage: payload.perPage ?? 10 },
			}),
			providesTags: ['MyBundles'],
		}),
		searchBundles: builder.query<ApiResponse<Bundle[]>, { searchQuery: string }>({
			query: payload => `/search/bundles?query=${payload.searchQuery ?? ''}`,
		}),
	}),
})

export const {
	useGetBundlesQuery,
	useGetFeaturedBundlesQuery,
	useGetBundleByLabelQuery,
	useGetMyBundlesQuery,
	useCheckoutBundleMutation,
	useLazyVerifyBundlePromocodeQuery,
	useGetBundleInvoicesQuery,
	useSearchBundlesQuery,
} = bundleApi
